import React, { useMemo } from "react";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useIntl } from "react-intl";
import styled from "styled-components";
import { CrudListUncontrolled } from "../../shared/crud/CrudList";
import { defaultListMessages } from "../../shared/crud/CrudList";
import { StringRenderer } from "../../shared/renderers/table";
import { TableRowActionProps } from "../../shared/actions";
import { AmountDelta, UpgradeTarget } from "../domain/upgradeConfiguration.schema";
import { upgradeManagerMessages, tableHeaderMessages } from "../upgradeManager.messages";
import { ActionButton } from "../styles";
import { getBaseTicker, formatDateRanges, formatDelta } from "../utils/formatters";

const tooltipEditMsg = { id: "app.entityList.tooltip.edit", defaultMessage: "Edit" };
const tooltipDuplicateMsg = { id: "app.entityList.tooltip.duplicate", defaultMessage: "Duplicate" };
const tooltipDeleteMsg = { id: "app.entityList.tooltip.delete", defaultMessage: "Delete" };

/**
 * Flat row type for CrudListUncontrolled.
 * CrudListUncontrolled requires an `id` field (Identifiable).
 * We use a synthetic `__idx_N` id to map back to the original array index.
 */
interface UpgradeTargetRow {
  id: string;
  targetType: string;
  baseTicker: string;
  upgradeTo: string;
  minDelta: string;
  maxDelta: string;
  includedDates: string;
  excludedDates: string;
  guarantee: string;
  penalty: string;
  includedTags: string;
  promoCodes: string;
}

interface TargetsTableProps {
  targets: UpgradeTarget[];
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
}

const targetsTableMessages = {
  ...defaultListMessages,
  entityName: upgradeManagerMessages.sellingTargets,
  noData: upgradeManagerMessages.noData,
  tableActions: upgradeManagerMessages.actions,
  tableHeaders: {
    targetType: upgradeManagerMessages.targetType,
    baseTicker: upgradeManagerMessages.base,
    upgradeTo: upgradeManagerMessages.upgradeTo,
    minDelta: tableHeaderMessages.minDelta,
    maxDelta: tableHeaderMessages.maxDelta,
    includedDates: tableHeaderMessages.includedDates,
    excludedDates: tableHeaderMessages.excludedDates,
    guarantee: upgradeManagerMessages.guarantee,
    penalty: upgradeManagerMessages.penalty,
    includedTags: upgradeManagerMessages.includedTags,
    promoCodes: upgradeManagerMessages.promoCodes,
  },
};

const targetsTableColumns = {
  targetType: StringRenderer,
  baseTicker: StringRenderer,
  upgradeTo: StringRenderer,
  minDelta: StringRenderer,
  maxDelta: StringRenderer,
  includedDates: StringRenderer,
  excludedDates: StringRenderer,
  guarantee: StringRenderer,
  penalty: StringRenderer,
  includedTags: StringRenderer,
  promoCodes: StringRenderer,
};

const HideHeadingWrapper = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;

  & > div > div:first-child {
    display: none;
  }
`;

export function TargetsTable({ targets, onEdit, onDelete, onDuplicate }: TargetsTableProps) {
  const intl = useIntl();

  const items: UpgradeTargetRow[] = useMemo(
    () =>
      targets.map((target, index) => ({
        id: `__idx_${index}`,
        targetType: target.targetType,
        baseTicker: getBaseTicker(target),
        upgradeTo: target.upgradeableToTickers?.join(", ") || "-",
        minDelta: target.sellingActivationRule?.minAmountDelta
          ? formatDelta(target.sellingActivationRule.minAmountDelta as AmountDelta)
          : "-",
        maxDelta: target.sellingActivationRule?.maxAmountDelta
          ? formatDelta(target.sellingActivationRule.maxAmountDelta as AmountDelta)
          : "-",
        includedDates: formatDateRanges(target.sellingActivationRule?.includedDates),
        excludedDates: formatDateRanges(target.sellingActivationRule?.excludedDates),
        guarantee: target.sellingActivationRule?.guaranteeApplied || "-",
        penalty: target.sellingActivationRule?.penaltyApplied || "-",
        includedTags: target.sellingActivationRule?.includedTags?.join(", ") || "-",
        promoCodes: target.sellingActivationRule?.promoCodes?.join(", ") || "-",
      })),
    [targets]
  );

  const RowActions = useMemo(
    () => ({ row, rest }: TableRowActionProps<UpgradeTargetRow>) => {
      const index = parseInt((row as UpgradeTargetRow).id.replace("__idx_", ""), 10);
      return (
        <Table.Cell {...rest}>
          <OverlayTrigger
            delay={{ show: 250, hide: 0 }}
            overlay={
              <Tooltip id={`tooltip-edit-${index}`}>{intl.formatMessage(tooltipEditMsg)}</Tooltip>
            }>
            <ActionButton size="sm" variant="default" onClick={() => onEdit(index)}>
              <i className="fa fa-pencil" />
            </ActionButton>
          </OverlayTrigger>
          <OverlayTrigger
            delay={{ show: 250, hide: 0 }}
            overlay={
              <Tooltip id={`tooltip-duplicate-${index}`}>
                {intl.formatMessage(tooltipDuplicateMsg)}
              </Tooltip>
            }>
            <ActionButton size="sm" variant="default" onClick={() => onDuplicate(index)}>
              <i className="fa fa-copy" />
            </ActionButton>
          </OverlayTrigger>
          <OverlayTrigger
            delay={{ show: 250, hide: 0 }}
            overlay={
              <Tooltip id={`tooltip-delete-${index}`}>
                {intl.formatMessage(tooltipDeleteMsg)}
              </Tooltip>
            }>
            <ActionButton size="sm" variant="danger" onClick={() => onDelete(index)}>
              <i className="fa fa-trash-o" />
            </ActionButton>
          </OverlayTrigger>
        </Table.Cell>
      );
    },
    [intl, onEdit, onDelete, onDuplicate]
  );

  return (
    <HideHeadingWrapper>
      <CrudListUncontrolled<UpgradeTargetRow>
        items={items}
        isLoading={false}
        columns={targetsTableColumns}
        hasPagination={false}
        hideExportButton
        fixedActions
        rowActions={RowActions}
        messages={targetsTableMessages}
        listHeight={Math.max(targets.length * 53 + 80, 120)}
        managerName="upgradeTargets"
      />
    </HideHeadingWrapper>
  );
}
